import React from 'react';
import { useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import {
  ArchiveIcon,
  Button,
  Checkbox, DisableIcon,
  IconButton,
  SearchIcon,
  Select,
  SymbolCircleIcon,
  Table,
  Text,
  TextInput,
  toaster, Tooltip,
} from 'evergreen-ui';
import {
  format, isAfter, isBefore, set, subDays,
} from 'date-fns';

import { useGetNotificationTypesQuery } from 'Services/api/notificationTypesApi';
import { useGetNotificationEventTypesQuery } from 'Services/api/notificationEventTypesApi';
import { isAdminSelector, userInfoSelector } from 'Services/auth.service';
import CheckboxList from 'Shared/CheckboxList';
import { VirtualList } from 'Utils/VirtualList';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { DATE_FORMATS } from 'Utils/formats';
import {
  useDeleteNotificationsSendingLogsMutation,
  useLazyGetNotificationSendingLogsQuery,
} from 'Services/api/notificationSendingLogsApi';
import { timeZoneSelector } from 'Features/App/appSlice';
import { timeZoneToUTC, UTCtoTimeZone } from 'Utils/UTCConverter';
import NotifyTable from 'Shared/Notifications/NotifyTable';
import { SearchSelectOnUsers } from 'Shared/SearchSelectOnUsers';
import { useGetUsersQuery } from 'Services/api/usersApi';

const useStyles = createUseStyles({
  notificationCell: {
    display: 'grid',
    gridTemplateColumns: '10em 1fr max-content',
    gridColumnGap: '.5rem',
    width: '100%',
    alignItems: 'center',
    paddingLeft: '.5rem',
    paddingRight: '.5rem',
    height: 'auto',
  },
  notificationSearch: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    alignItems: 'center',
    justifyContent: 'center',
    gridColumnGap: '.5rem',
  },
  notificationWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
  },
  notificationsTable: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    gridRowGap: '.5rem',
  },
  notificationTextTableCellUnread: {
    fontWeight: '600',
  },
  notificationTextTableCellRead: {
    fontWeight: 'normal',
  },
  buttonArchiveStyle: {
    color: 'grey',
  },
});

const PATH = {
  users: `${process.env.REACT_APP_AUTH_PREFIX}/keycloak/users`,
  userActionTypes: '/useractiontypes',
  logs: (username, actions) => `/user-actions?userName=${username}&${actions}`,
};

const INPUT_DATETIME = "yyyy-MM-dd'T'HH:mm"; // value внутри инпута datetime
const pageSizes = [10, 20, 50, 100];

const initialParams = {
  startDate: format(subDays(set(new Date(), { hours: 0, minutes: 0, seconds: 0 }), 7), INPUT_DATETIME),
  endDate: format(set(new Date(), { hours: 23, minutes: 59, seconds: 59 }), INPUT_DATETIME),
  size: pageSizes[2],
};

export default function NotificationsArchive({ isSettings = false, timeZone }) {
  const selectedZone = useSelector(timeZoneSelector);
  const isAdmin = useSelector(isAdminSelector);
  const { data: notificationTypes = [] } = useGetNotificationTypesQuery();
  const { data: notificationEventTypes = [] } = useGetNotificationEventTypesQuery();
  const [loadNotificationLogs, {
    data: notifications,
    isLoading: isLoadingNotificationLogs,
    isFetching: isFetchingNotificationLogs,
  }] = useLazyGetNotificationSendingLogsQuery();
  const [deleteNotificationLogs] = useDeleteNotificationsSendingLogsMutation();

  const { currentUser } = useSelector(userInfoSelector);
  const [selectedUser, setSelectedUser] = React.useState(-1);
  const { data: users = [] } = useGetUsersQuery(PATH.users);

  const [selectedNotify, setSelectedNotify] = React.useState([]);
  const tableHeader = [
    { name: `Дата UTC${selectedZone.selectedUserTimeZone}`, cellProps: { flex: '0 0 10%' } },
    { name: 'Реакция пользователя', cellProps: { flex: '0 0 13%', justifyContent: 'left' } },
    { name: 'Тип уведомления', cellProps: { flex: '0 0 20%', justifyContent: 'left' } },
    { name: 'Уведомление', cellProps: { flex: '2 2 37%', justifyContent: 'left' } },
  ];
  const [params, setParams] = React.useState({
    size: initialParams.size,
    startDate: timeZoneToUTC(new Date(initialParams.startDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
    endDate: timeZoneToUTC(new Date(initialParams.endDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
  });
  const maxEndDate = React.useMemo(() => format(new Date(), INPUT_DATETIME), []);
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSizes[2]);
  const [currentPage, setCurrentPage] = React.useState(0);

  const [dialogData, setDialogData] = React.useState(null);

  const [selectedNotifyTypesCodes, setSelectedNotifyTypesIds] = React.useState([]);
  const [selectedNotifyEventIds, setSelectedNotifyEventIds] = React.useState([]);

  const classes = useStyles();

  const activeNotify = React.useMemo(() => notifications?.content?.filter((i) => i.notificationAppLog).map((i) => i.notificationAppLog?.id), [notifications]);
  const disabled = React.useMemo(() => isLoadingNotificationLogs || !selectedNotifyTypesCodes.length || !selectedNotifyEventIds.length, [isLoadingNotificationLogs, selectedNotifyEventIds.length, selectedNotifyTypesCodes.length]);

  React.useEffect(() => {
    if (notificationTypes?.length) {
      setSelectedNotifyTypesIds(notificationTypes?.map(({ name, code }) => ({ label: name, value: code })) || []);
    }
  }, [notificationTypes]);

  React.useEffect(() => {
    if (notificationEventTypes?.length) {
      setSelectedNotifyEventIds(notificationEventTypes?.map(({ name, code }) => ({ label: name, value: code })) || []);
    }
  }, [notificationEventTypes]);

  const onChangeDate = React.useCallback((e) => {
    const { name, value } = e.target;
    setParams((d) => {
      if (name === 'startDate' && isAfter(new Date(value), new Date(d.endDate))) {
        toaster.danger('Время начала должно быть меньше конечного', { id: name });
        return d;
      }
      if (name === 'endDate' && isBefore(new Date(value), new Date(d.startDate))) {
        toaster.danger('Время конца должно быть больше начального', { id: name });
        return d;
      }
      return { ...d, [name]: value };
    });
  }, []);

  const setChangedParams = React.useCallback((obj = {}) => {
    setParams((curr) => ({
      size: curr.size,
      startDate: curr.startDate,
      endDate: curr.endDate,
      ...obj,
    }));
  }, []);

  const selectPageSize = React.useCallback((e) => {
    setCurrentPageSize(e.target.value);
  }, []);

  // option для чекбоксов
  const notifyEventAllTypesOption = React.useMemo(() => notificationEventTypes.map(({ code, name }) => ({
    label: name,
    value: code,
  })), [notificationEventTypes]);
  const selectNotifyTypesOption = React.useMemo(() => notificationTypes.map(({ code, name }) => ({
    label: name,
    value: code,
  })), [notificationTypes]);

  // на кнопку поиска/смены страницы
  const onSearchClick = React.useCallback((page) => {
    const fetchParams = {
      ...params,
      startDate: timeZoneToUTC(new Date(params.startDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
      endDate: timeZoneToUTC(new Date(params.endDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
      page: Number.isNaN(+page) ? 0 : page,
      size: currentPageSize,
      ...(selectedNotifyEventIds.length !== notifyEventAllTypesOption.length
        ? { eventTypes: selectedNotifyEventIds.map((item) => item.value).join(',') }
        : {}),
      ...(selectedNotifyTypesCodes.length !== selectNotifyTypesOption.length
        ? { notificationTypes: selectedNotifyTypesCodes.map((item) => item.value).join(',') }
        : {}),
      userName: isAdmin && +selectedUser === -1
        ? currentUser?.username
        : selectedUser,
    };

    loadNotificationLogs(fetchParams);

    setChangedParams({ size: currentPageSize });
    setCurrentPage(page);
  }, [currentPageSize, currentUser?.username, isAdmin, loadNotificationLogs, notifyEventAllTypesOption.length, params, selectNotifyTypesOption.length, selectedNotifyEventIds, selectedNotifyTypesCodes, selectedUser, selectedZone.selectedUserTimeZone, setChangedParams]);

  const onPageChange = React.useCallback((page) => {
    onSearchClick(page);
    setCurrentPage(page);
  }, [onSearchClick]);

  // поиск по юзеру
  React.useEffect(() => {
    if (currentUser) setSelectedUser(currentUser?.username);
  }, [currentUser]);

  const onChangeUser = React.useCallback((event) => {
    if (event !== null) {
      setSelectedUser(event?.username || '');
    } else setSelectedUser(null);
  }, [setSelectedUser]);

  // Подгрузка уведомлений при открытии таба
  React.useEffect(() => {
    loadNotificationLogs({
      size: params.size,
      startDate: timeZoneToUTC(new Date(initialParams.startDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
      endDate: timeZoneToUTC(new Date(initialParams.endDate), selectedZone.selectedUserTimeZone, DATE_FORMATS.api),
    });
  }, [loadNotificationLogs, params.size, selectedZone.selectedUserTimeZone]);

  const onChangeChecked = React.useCallback((e, id) => {
    setSelectedNotify((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (e.target?.checked) tmp.push(id);
      else tmp.splice(tmp.findIndex((i) => i === id), 1);
      return tmp;
    });
  }, []);

  const RowTable = React.useCallback((notification) => {
    const notifyCreateDateTimeUTCArchive = UTCtoTimeZone(notification?.creationDateTimeUTC, selectedZone.selectedUserTimeZone);
    const createMarkup = () => {
      const newstr = notification?.text?.replace(/\n/g, '<br />');

      return {
        __html: newstr?.length > 60 ? `${newstr.substring(0, 60)}...` : newstr ?? '',
      };
    };
    const onChange = (event) => {
      event.persist();
      onChangeChecked(event, notification?.notificationAppLog?.id);
    };

    const WatchNotificationsReaction = () => {
      if (notification?.read === true) {
        return (
          <Tooltip
            content="Пользователь отреагировал на уведомление"
          >
            <IconButton
              appearance="minimal"
              iconSize={20}
              icon={SymbolCircleIcon}
              intent="success"
            />
          </Tooltip>
        );
      } if (notification?.read === false) {
        return (
          <Tooltip
            content="Пользователь не отреагировал на уведомление"
          >
            <IconButton
              appearance="minimal"
              iconSize={20}
              icon={SymbolCircleIcon}
              intent="danger"
            />
          </Tooltip>
        );
      }
      return (
        <Tooltip
          content="Контроль не предусмотрен"
        >
          <IconButton
            appearance="minimal"
            iconSize={20}
            icon={DisableIcon}
          />
        </Tooltip>
      );
    };

    return (
      <Table.Row
        isSelectable
        height="auto"
        intent={notification?.notification?.appLog?.length ? 'success' : 'none'}
        onClick={() => setDialogData({ isShown: true, notification })}
      >
        <Table.Cell flex="10em min-content">
          <Checkbox
            onClick={(e) => {
              e.stopPropagation();
            }}
            disabled={!notification?.notificationAppLog}
            checked={!!selectedNotify.find((i) => i === notification?.notificationAppLog?.id)}
            onChange={onChange}
          />
        </Table.Cell>
        <Table.Cell flex="0 0 10%">
          <div className={notification?.notificationAppLog?.id
            ? classes.notificationTextTableCellUnread
            : classes.notificationTextTableCellRead}
          >
            {
              notifyCreateDateTimeUTCArchive
            }
          </div>
        </Table.Cell>
        <Table.Cell flex="1 1 12%">
          <WatchNotificationsReaction />
        </Table.Cell>
        <Table.Cell flex="0 1 20%">
          <div className={notification?.notificationAppLog?.id
            ? classes.notificationTextTableCellUnread
            : classes.notificationTextTableCellRead}
          >
            {`${notification.notificationType?.name} ${notification.notificationType?.code}`}
          </div>
        </Table.Cell>
        <Table.Cell flex="1 1 43%">
          <Text
            className={notification?.notificationAppLog?.id
              ? classes.notificationTextTableCellUnread
              : classes.notificationTextTableCellRead}
            dangerouslySetInnerHTML={createMarkup()}
          />
        </Table.Cell>
        <Table.Cell flex="1 3 10%">
          <Button
            className={!notification?.notificationAppLog ? classes.buttonArchiveStyle : ''}
            appearance="minimal"
            iconBefore={ArchiveIcon}
            onClick={() => setDialogData({ isShown: true, notification })}
          >
            {!notification?.notificationAppLog ? 'Просмотреть' : 'Прочитать'}
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  }, [classes.buttonArchiveStyle, classes.notificationTextTableCellRead, classes.notificationTextTableCellUnread, onChangeChecked, selectedNotify, selectedZone.selectedUserTimeZone]);

  const deleteNotifications = React.useCallback(async (ids) => {
    try {
      await deleteNotificationLogs({
        body: ids,
        userName: currentUser.username,
      }).unwrap();
      toaster.success(ids.length > 1 ? 'Уведомления отмечены как прочитанные!' : 'Уведомление отмечено как прочитанное!');
      onSearchClick(0);
      setCurrentPage(0);
    } catch {
      toaster.danger(ids.length > 1
        ? 'Ошибка при перемещении уведомлений в прочитанные!'
        : 'Ошибка при перемещении уведомления в прочитанные!');
    }
  }, [currentUser.username, deleteNotificationLogs, onSearchClick]);

  const onCheckedAll = (event) => {
    if (event.target.checked) setSelectedNotify(activeNotify);
    else setSelectedNotify([]);
  };

  // notifyDialog
  const onClicked = () => {
    setDialogData({ isShown: true, notifications: selectedNotify });
  };
  const onConfirmDialogData = () => {
    deleteNotifications(dialogData?.notifications?.length > 0
      ? [...dialogData?.notifications]
      : [dialogData?.notification?.notificationAppLog?.id]);
    setDialogData(null);
  };

  // кнопка прочитать disabled у прочитанных уведомлений
  const isConfirmDisabled = React.useMemo(() => (
    (!dialogData?.notifications && !dialogData?.notification?.notificationAppLog?.id)
    || (!dialogData && !(dialogData.notificationType?.code === 'APP_HYDROPAD'
      || dialogData.notificationType?.code === process.env.REACT_APP_NOTIFICATION_TYPE_APP_CODE))
  ), [dialogData]);

  return (
    <div className={notifications?.content?.length > 0 ? classes.notificationsTable : classes.notificationsTable}>
      <div className={classes.notificationSearch}>

        <Text>с</Text>
        <TextInput
          width="auto"
          type="datetime-local"
          name="startDate"
          value={params.startDate}
          onChange={onChangeDate}
          max={params.endDate}
        />
        <Text>по</Text>
        <TextInput
          width="auto"
          type="datetime-local"
          name="endDate"
          value={params.endDate}
          onChange={onChangeDate}
          min={params.startDate}
          max={maxEndDate}
        />

        <Text>показывать по</Text>
        <Select
          value={currentPageSize}
          onChange={selectPageSize}
        >
          {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
        </Select>

        {
          !isAdmin || !isSettings
            ? null
            : (
              <SearchSelectOnUsers
                users={users}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                onChangeUser={onChangeUser}
                initialSelectedItem={{ email: currentUser.emailID, ...currentUser }}
              />
            )
         }

        <CheckboxList
          options={selectNotifyTypesOption}
          selectedItems={selectedNotifyTypesCodes}
          setSelectedItems={setSelectedNotifyTypesIds}
          tooltip="Выберите типы уведомлений"
          intent="none"
          appearance="default"
        />
        <CheckboxList
          options={notifyEventAllTypesOption}
          selectedItems={selectedNotifyEventIds}
          setSelectedItems={setSelectedNotifyEventIds}
          tooltip="Выберите события"
          intent="none"
          appearance="default"
        />
        <IconButton
          icon={SearchIcon}
          disabled={disabled || isFetchingNotificationLogs}
          appearance="primary"
          intent="success"
          onClick={() => onSearchClick(0)}
        />
      </div>
      {
        !isLoadingNotificationLogs && !isFetchingNotificationLogs
          ? (
            <NotifyTable
              checked={selectedNotify?.length > 0 && selectedNotify.length === activeNotify?.length}
              onCheckedAll={onCheckedAll}
              thead={tableHeader}
              onClickedButton={onClicked}
              disabledCheckBox={!notifications?.content || !activeNotify.length}
              disabledButton={!notifications?.content || !selectedNotify.length}
              isShownDialog={dialogData?.isShown}
              dialogData={dialogData?.notification || {}}
              closeDialog={() => setDialogData(null)}
              multiple={!!dialogData?.notifications}
              onConfirmDialogData={onConfirmDialogData}
              isConfirmDisabled={isConfirmDisabled}
              currentPage={currentPage}
              onPageChange={onPageChange}
              totalPages={+notifications?.totalPages}
              isSettings={isSettings}
            >
              <VirtualList data={notifications?.content || []} row={RowTable} />
            </NotifyTable>
          )
          : <SuspenseSpinner />
      }
    </div>
  );
}
